/**
 * Power Query Preview Service
 *
 * Handles generation of Power Query (M language) export script previews
 */

import { replacePlaceholders } from '../utils/replacePlaceholders';

import type {
  PreviewGenerationContext,
  PreviewResult,
  TemplateBasedPreviewService
} from './PreviewService.types';

export class PowerQueryPreviewService implements TemplateBasedPreviewService {
  /**
   * Generate Power Query preview from template and API URL
   */
  generateFromTemplate(
    template: string,
    context: PreviewGenerationContext,
    exportApiUrl?: string
  ): PreviewResult {
    try {
      if (!template) {
        return {
          content: '',
          error: 'Template not provided'
        };
      }

      if (!exportApiUrl) {
        return {
          content: '',
          error: 'Export API URL required for Power Query preview'
        };
      }

      // Modify URL to ensure metadata=true
      const url = exportApiUrl
        .split('&')
        .filter(str => !str.includes('metadata'))
        .join('&')
        .concat('&metadata=true');

      // Generate row filters for Power Query
      const rowFilters = context.symbols
        .map(
          symbol =>
            `(Text.From([Code]) = "${symbol.Code}" and Text.From([TimeRef]) = "${symbol.TimeRef}" and Text.From([PeriodType]) = "${symbol.PeriodType}" and Text.From([Period]) = "${symbol.Period}")`
        )
        .join(' or \n');

      const replacements = {
        apiUrl: url,
        rowFilters,
        apiKey: context.apiKey
      } as const;

      const content = replacePlaceholders(template, replacements);

      return { content };
    } catch (error) {
      return {
        content: '',
        error: error instanceof Error ? error.message : 'Failed to generate Power Query preview'
      };
    }
  }

  /**
   * Generate preview (requires template and API URL to be provided separately)
   */
  generatePreview(_context: PreviewGenerationContext): PreviewResult {
    return {
      content: '',
      error:
        'Power Query preview requires a template and API URL. Use generateFromTemplate() instead.'
    };
  }
}

// Export singleton instance
export const powerQueryPreviewService = new PowerQueryPreviewService();
